<template>
    <div
        class="gallery-viewer"
        v-if="!loading">

        <section class="panel media-container">
            <div class="panel-heading">
                <h2>
                    <i class="fa fa-image"></i>
                    Media
                </h2>
            </div>
            <div class="panel-body">
                <ol>
                    <li
                        class="media-group"
                        :key="media.id"
                        v-for="(media, index) in secondaryMedia">

                        <div class="media-wrapper">
                            <img
                                class="media"
                                v-if="media.type === 'image'"
                                :src="media.url"
                                @click="openLightbox(index)">

                            <div v-if="media.type === 'video'">
                                <div
                                    class="media media-iframe"
                                    v-if="!media.iframeHtmlError"
                                    @click="openLightbox(index)">
                                    <div v-html="media.iframeHtml"></div>
                                    <div class="media-iframe-spacer-16-9"></div>
                                </div>
                                <div
                                    class="media-error"
                                    v-else>
                                    <p>Error loading video</p>
                                </div>
                            </div>

                            <div v-if="media.type === 'sketchfab'">
                                <div
                                    class="media media-iframe"
                                    v-if="!media.iframeHtmlError"
                                    @click="openLightbox(index)">
                                    <div v-html="media.iframeHtml"></div>
                                    <div class="media-iframe-spacer-16-9"></div>
                                </div>
                                <div
                                    class="media-error"
                                    v-else>
                                    <p>Error loading Sketchfab</p>
                                </div>
                            </div>

                            <div v-if="media.type === 'marmoset'">
                                <div class="media media-iframe">
                                    <marmoset-viewer :src="media.url"></marmoset-viewer>
                                    <div class="media-iframe-spacer-8-6"></div>
                                </div>
                            </div>

                            <div class="media-buttons-wrapper">
                                <div
                                    class="media-buttons"
                                    v-if="media.type === 'image'">
                                    <a
                                        target="_blank"
                                        :href="media.url">
                                        <i class="fa fa-arrow-down"></i>
                                    </a>
                                    <button
                                        class="clear-button"
                                        @click="openLightbox(index)">
                                        <i class="fa fa-expand"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div
                            class="media-caption"
                            v-if="!!media.caption"
                            v-html="$options.filters.linkify(escapeHtml(media.caption))">
                        </div>
                    </li>
                </ol>
            </div>
        </section>

        <div class="right-container">
            <section class="panel info-container">
                <div class="panel-heading">
                    <h2>
                        <i class="fa fa-info-circle"></i>
                        Details
                    </h2>
                    <router-link
                        :to="{ name: 'edit-gallery', params: { galleryId: id } }"
                        v-if="canEdit">
                        Edit
                    </router-link>
                </div>

                <div class="panel-body">
                    <h3>{{ title }}</h3>

                    <div
                        class="form-group recruiter-visibility"
                        v-if="canEditHighlight">
                        <label>
                            <input
                                type="checkbox"
                                :disabled="savingHighlight"
                                v-model="highlighted">
                            Visible to recruiters
                        </label>
                    </div>

                    <div class="post-author">
                        By
                        <router-link :to="{ name: 'galleries', query: { author: author.id } }">
                            {{ author.name }}
                        </router-link>

                        <a 
                        v-if="localStorage.roles.includes('Recruiter')"
                        :href="'mailto:'+author.email"
                        target="_blank"
                        >
                            {{author.email}}
                        </a>
                    </div>

                    <p
                        class="post-description"
                        v-if="!!description"
                        v-html="$options.filters.linkify(description)"></p>

                    <div class="post-date">
                        Posted {{ formatPostDate(createdAt) }}
                    </div>

                    <div class="info-group gallery-stats">
                        <div>
                            <i class="fa fa-thumbs-up"></i>
                            <span>{{ formatStatText('Like', 'Likes', likes.length) }}</span>
                            <button
                                class="clear-button"
                                type="button"
                                :disabled="submittingLike"
                                v-if="!hasLikedGallery"
                                @click="likeGallery">
                                Like
                            </button>
                            <button
                                class="clear-button"
                                type="button"
                                :disabled="submittingLike"
                                v-else
                                @click="unlikeGallery">
                                Unlike
                            </button>
                        </div>
                        <div>
                            <i class="fa fa-eye"></i>
                            <span>{{ formatStatText('View', 'Views', viewCount) }}</span>
                        </div>
                        <div>
                            <i class="fa fa-comments"></i>
                            <span>{{ formatStatText('Comment', 'Comments', commentCount) }}</span>
                        </div>
                    </div>

                    <div
                        class="info-group"
                        v-if="term || path || groups.length > 0 || assignment">
                        <div class="info-group-label">Categories</div>

                        <div
                            class="category"
                            v-if="!!term">
                            <div>Term</div>
                            <router-link :to="{ name: 'galleries', query: { term: '' + term.id }}">
                                {{ term.name }}
                            </router-link>
                        </div>

                        <div
                            class="category"
                            v-if="!!path && path.id != 9200">
                            <div>Path</div>
                            <router-link :to="{ name: 'galleries', query: { path: '' + path.id }}">
                                {{ path.name }}
                            </router-link>
                        </div>

                        <div
                            class="category"
                            v-if="groups.length > 0">
                            <div v-if="groups.length > 1">Groups</div>
                            <div v-else>Group</div>
                            <router-link
                                :to="{ name: 'galleries', query: { group: '' + group.id }}"
                                :key="group.id"
                                v-for="group in groups">
                                {{ group.label }}
                            </router-link>
                        </div>

                        <div
                            class="category"
                            v-if="!!assignment">
                            <div>Assignment</div>
                            <router-link :to="{ name: 'galleries', query: { assignment: '' + assignment.id }}">
                                {{ assignment.name }}
                            </router-link>
                        </div>
                    </div>

                    <div
                        class="info-group"
                        v-if="softwareUsed.length > 0">
                        <div class="info-group-label">Software Used</div>
                        <software-list :value="linkedSoftwareUsed">
                        </software-list>
                    </div>

                    <div
                        class="info-group more-by"
                        v-if="moreGalleries.length > 0">
                        <div class="info-group-label">
                            <span>More by</span>
                            <router-link :to="{ name: 'galleries', query: { author: author.id } }">
                                {{ author.name }}
                            </router-link>
                        </div>
                        <gallery-grid
                            :gutter-width="10"
                            :count-per-row="3"
                            :galleries="moreGalleries"
                            @click="onAuthorGalleryClick">
                        </gallery-grid>
                    </div>

                    <div
                        class="info-group"
                        v-if="tags.length > 0">
                        <div class="info-group-label">Tags</div>
                        <tag-list :value="linkedTags">
                        </tag-list>
                    </div>
                </div>
            </section>

            <section class="panel comments-container">
                <div class="panel-heading">
                    <h2>
                        <i class="fa fa-comments"></i>
                        {{ formatCommentsText(commentCount) }}
                    </h2>
                </div>

                <div class="panel-body">
                    <ol>
                        <li
                            :key="comment.id"
                            v-for="comment in comments">
                            <gallery-comment
                                class="comment"
                                :comment="comment"
                                @like="likeComment"
                                @unlike="unlikeComment"
                                @edit="editComment"
                                @reply="addCommentReply"
                                @delete="deleteComment"
                                @focus="addCommentReplyFocus"
                                @blur="addCommentReplyBlur">
                            </gallery-comment>
                        </li>
                    </ol>

                    <gallery-comment-form
                        placeholder="Add a comment..."
                        @submit="addComment"
                        @focus="addCommentReplyFocus"
                        @blur="addCommentReplyBlur">
                    </gallery-comment-form>
                </div>
            </section>
        </div>

        <LightboxMediaGallery
            :media="lightbox.media"
            :index.sync="lightbox.index"
            :showing.sync="lightbox.showing"
            :can-slide-past-first="true"
            :can-slide-past-last="true"
            @slide-past-first="goToLeftGallery()"
            @slide-past-last="goToRightGallery()"
            v-if="lightbox.media.length > 0">
        </LightboxMediaGallery>
    </div>

    <div class="gallery-viewer" v-else>
        <div class="panel loading-container">
            <div class="panel-body">
                Loading
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import toastr from 'toastr';

    import Galleries            from '../services/galleries';
    import Noembed              from '../services/noembed';
    import Sketchfab            from '../services/sketchfab';
    import GalleryUtils         from '../utils/galleries';
    import LightboxMediaGallery from './reusable/lightbox/media-gallery';
    import SoftwareList         from './reusable/software-list';
    import TagList              from './reusable/tag-list';
    import GalleryComment       from './widgets/GalleryComment';
    import GalleryCommentForm   from './widgets/GalleryCommentForm';
    import GalleryGrid          from './widgets/GalleryGrid';
    import GallerySoftwareList  from './widgets/GallerySoftwareList';
    import GalleryTagList       from './widgets/GalleryTagList';
    import MarmosetViewer       from './widgets/MarmosetViewer';

    const { PaginatedGalleryList } = GalleryUtils;


    export default {
        components: {
            LightboxMediaGallery,
            SoftwareList,
            TagList,
            GalleryComment,
            GalleryCommentForm,
            GalleryGrid,
            GallerySoftwareList,
            GalleryTagList,
            MarmosetViewer,
        },

        data() {
            return {
                gallery         : null,
                comments        : [],
                commentsInFocus : 0,
                moreGalleries   : [],
                leftGalleryId   : null,
                rightGalleryId  : null,
                cycleContext    : null,
                highlighted     : false,
                submittingLike  : false,
                savingHighlight : false,
                loading         : true,
                lightbox        : {
                    showing       : false,
                    media         : [],
                    index         : 0,
                },
            };
        },

        computed: {
            id() {
                return this.gallery ? this.gallery.id : null;
            },

            title() {
                return this.gallery ? this.gallery.title : null;
            },

            description() {
                return this.gallery
                    ? this.escapeHtml(this.gallery.description)
                    : null;
            },

            term() {
                return this.gallery ? this.gallery.term : null;
            },

            path() {
                return this.gallery ? this.gallery.path : null;
            },

            assignment() {
                return this.gallery ? this.gallery.assignment : null;
            },

            author() {
                return this.gallery ? this.gallery.author : null;
            },

            groups() {
                return this.author ? this.author.groups : [];
            },

            softwareUsed() {
                return this.gallery ? this.gallery.softwareUsed : [];
            },

            tags() {
                return this.gallery ? this.gallery.tags : [];
            },

            secondaryMedia() {
                return this.gallery ? this.gallery.secondaryMedia : [];
            },

            likes() {
                return this.gallery ? this.gallery.likes : [];
            },

            viewCount() {
                return this.gallery ? this.gallery.viewCount : 0;
            },

            createdAt() {
                return this.gallery ? this.gallery.createdAt : null;
            },

            commentCount() {
                let countComments;
                countComments = comments => {
                    let sum = comments.length;
                    comments.forEach(c => sum += countComments(c.replies));
                    return sum;
                };
                return countComments(this.comments);
            },

            hasLikedGallery() {
                const userId = this.userId;
                return !!this.likes.find(l => l.id === userId);
            },

            linkedSoftwareUsed() {
                return this.softwareUsed.map(s => {
                    return Object.assign({
                        href: '/galleries?software=' + s.id,
                    }, s);
                });
            },

            linkedTags() {
                return this.tags.map(t => {
                    return Object.assign({
                        href: '/galleries?tags=' + t.id,
                    }, t);
                });
            },

            userId() {
                return JSON.parse(localStorage.user_id);
            },

            userRoles() {
                return JSON.parse(localStorage.roles);
            },

            canEdit() {
                const isAuthor = this.userId === this.author.id;
                const isMod = !!this.userRoles.find(r => {
                    return r === 'OnlineDirector' || r === 'Supervisor';
                });
                return isAuthor || isMod;
            },

            canEditHighlight() {
                return this.userRoles.includes('OnlineDirector');
            },
        },

        async mounted() {
            let galleryId = this.$route.params.galleryId;
            if (galleryId) {
                galleryId = parseInt(galleryId);
            }
            await this.loadGallery(galleryId);

            window.addEventListener('keyup', this.onKeyup);
        },

        beforeDestroy() {
            window.removeEventListener('keyup', this.onKeyup);
        },

        methods: {
            onKeyup(ev) {
                if (this.lightbox.showing) {
                    return;
                }
                if (this.commentsInFocus > 0) {
                    return;
                }
                switch (ev.key) {
                case 'ArrowLeft':
                    this.goToLeftGallery();
                    break;
                case 'ArrowRight':
                    this.goToRightGallery();
                    break;
                }
            },

            async loadLeftRightGalleries() {
                this.leftGalleryId = null;
                this.rightGalleryId = null;

                // When a user clicks on a gallery in some other component, like
                // the gallery list viewer, the context is saved. This allows us
                // to cycle through that context in the viewer.
                const savedContext = await this.$store.dispatch(
                    'popGalleryViewerContext'
                );
                if (savedContext) {
                    this.cycleContext = new PaginatedGalleryList(
                        savedContext.params,
                        { initialItems: savedContext.initial },
                    );
                }

                // If no context exists, we'll fallback to cycling through the
                // author's galleries.
                if (!this.cycleContext) {
                    this.cycleContext = new PaginatedGalleryList({
                        author: this.author.id,
                    });
                }

                // Using our context, load up to this gallery + 1 (to account
                // for the next/"right" gallery).
                const findNextGallery = () => {
                    let foundUs = false;
                    return gallery => {
                        if (foundUs) {
                            return true;
                        }
                        if (gallery.id === this.id) {
                            foundUs = true;
                        }
                        return false;
                    };
                };
                await this.cycleContext.find(findNextGallery());

                // Save the left and right gallery ids, if they're available in
                // our context.
                const list = this.cycleContext.galleries;
                const index = list.findIndex(g => g.id === this.id);
                if (index >= 0) {
                    if (list[index - 1]) {
                        this.leftGalleryId = list[index - 1].id;
                    }
                    if (list[index + 1]) {
                        this.rightGalleryId = list[index + 1].id;
                    }
                }
            },

            async loadGallery(galleryId) {
                this.loading = true;

                // Load the gallery
                try {
                    this.gallery = await Galleries.getGallery(galleryId);

                    this.comments      = [];
                    this.moreGalleries = [];
                    this.highlighted   = this.gallery.highlighted;

                    // Load the media into the lightbox
                    this.lightbox.media = this.secondaryMedia.slice();

                    // Get html for videos and sketchfab media
                    const promises = [];
                    promises.push((async () => {
                        await this.secondaryMedia.map(async media => {
                            if (media.type === 'video') {
                                media.iframeHtml = null;
                                media.iframeHtmlError = null;
                                await Noembed.getInfo(media.url).then(info => {
                                    media.iframeHtml = info.html.replace(/(width|height)=".+?"/g, '');
                                }, err => {
                                    media.iframeHtmlError = err;
                                });

                            } else if (media.type === 'sketchfab') {
                                media.iframeHtml = null;
                                media.iframeHtmlError = null;
                                await Sketchfab.getInfo(media.url).then(info => {
                                    media.iframeHtml = info.html.replace(/(width|height)=".+?"/g, '');
                                }, err => {
                                    media.iframeHtmlError = err;
                                });
                            }
                        });
                    })());

                    // Get gallery comments
                    promises.push((async () => {
                        this.comments = await Galleries.getGalleryComments(this.id);
                    })());

                    // Get more galleries by this author. There's no api
                    // endpoint for this, so what we do is get 50 of the user's
                    // most recent galleries and pick a max of 9 of them at
                    // random, making sure not to include the current gallery.
                    promises.push((async () => {

                        // Get latest galleries
                        const res = await Galleries.getGalleries({
                            author: this.author.id,
                            limit: 50,
                            order: 'latest',
                        });

                        // Filter out current gallery
                        const opts = res.galleries.filter(gallery => {
                            return gallery.id !== this.id;
                        });

                        // Pick (at most) 9 random galleries
                        const picks = [];
                        while (opts.length > 0 && picks.length < 9) {
                            const optCount = opts.length;
                            const index = Math.floor(Math.random() * optCount);
                            picks.push(opts.splice(index, 1)[0]);
                        }
                        this.moreGalleries = picks;
                    })());

                    // Get the left and right gallery ids (if available)
                    promises.push(this.loadLeftRightGalleries());

                    // All of the operations above were added to the promises
                    // array, so that they can all be done in parallel. Now
                    // we'll wait for all of them to finish.
                    await Promise.all(promises);

                } catch (err) {
                    toastr.error(`
                        There was an error while loading the gallery.
                        Please try again later.
                    `);
                    throw err;

                } finally {
                    this.loading = false;
                }

                // Now that we're done loading, add a view. Attempting to add 2
                // views within 15 minutes will cause an error.
                await Galleries.viewGallery(this.id).then(viewCount => {
                    this.gallery.viewCount = viewCount;
                }, err => {
                    if (!err.viewExists) {
                        console.log('Failed to record gallery view:', err);
                    }
                });
            },

            async saveHighlight() {
                if (this.gallery.highlighted === this.highlighted) {
                    return;
                }
                try {
                    this.savingHighlight = true;

                    await Galleries.updateGallery(this.id, {
                        highlighted: this.highlighted,
                    });

                    this.gallery.highlighted = this.highlighted;

                    if (this.highlighted) {
                        toastr.success('Gallery is now visible to recruiters.');
                    } else {
                        toastr.success('Gallery is no longer visible to recruiters.');
                    }

                } catch (err) {
                    console.log('Error updating recruiter visibility:', err);
                    toastr.error(`
                        There was an error while updating the recruiter visibility.
                        Please try again later.
                    `);
                    this.highlighted = this.gallery.highlighted;

                } finally {
                    this.savingHighlight = false;
                }
            },

            async goToLeftGallery() {
                if (this.leftGalleryId) {
                    this.$router.push({
                        name: 'gallery',
                        params: {
                            galleryId: this.leftGalleryId,
                        },
                    });
                    this.lightbox.index = 0;
                }
            },

            async goToRightGallery() {
                if (this.rightGalleryId) {
                    this.$router.push({
                        name: 'gallery',
                        params: {
                            galleryId: this.rightGalleryId,
                        },
                    });
                    this.lightbox.index = 0;
                }
            },

            onAuthorGalleryClick() {
                this.$store.dispatch('pushGalleryViewerContext', {
                    params: {
                        author: this.author.id,
                    },
                });
            },

            formatPostDate(date) {
                return moment(date).fromNow();
            },

            formatStatText(singularText, pluralText, number) {
                if (number === 1) {
                    return `${number} ${singularText}`;
                } else {
                    return `${number} ${pluralText}`;
                }
            },

            formatCommentsText(commentCount) {
                if (commentCount === 0) {
                    return 'Comments';
                } else if (commentCount === 1) {
                    return '1 Comment';
                } else {
                    return `${commentCount} Comments`;
                }
            },

            async likeGallery() {
                this.submittingLike = true;
                try {
                    const userId = this.userId;
                    const likes = await Galleries.likeGallery(this.id, userId);
                    this.gallery.likes = likes;

                } catch (err) {
                    console.log('Error liking gallery:', err);
                    toastr.error(`
                        There was an error while liking the gallery.
                        Please try again later.
                    `);

                } finally {
                    this.submittingLike = false;
                }
            },

            async unlikeGallery() {
                this.submittingLike = true;
                try {
                    const userId = this.userId;
                    const likes = await Galleries.unlikeGallery(this.id, userId);
                    this.gallery.likes = likes;

                } catch (err) {
                    console.log('Error unliking gallery:', err);
                    toastr.error(`
                        There was an error while unliking the gallery.
                        Please try again later.
                    `);

                } finally {
                    this.submittingLike = false;
                }
            },

            async addComment(text, setSubmitting, clearText) {
                setSubmitting(true);
                try {
                    const comment = await Galleries.addGalleryComment(this.id, {
                        text: text,
                        createdBy: this.userId,
                    });
                    clearText();
                    setSubmitting(false);
                    this.comments.push(comment);

                } catch (err) {
                    console.log('Error creating comment:', err);
                    toastr.error(`
                        There was an error while creating the comment.
                        Please try again later.
                    `);
                    setSubmitting(false);
                }
            },

            async addCommentReply(parentCommentId, text, setSubmitting, clearText, hideReplyForm) {
                setSubmitting(true);
                try {
                    const comment = await Galleries.addGalleryComment(this.id, {
                        text: text,
                        createdBy: this.userId,
                        parentCommentId: parentCommentId
                    });
                    clearText();
                    setSubmitting(false);
                    hideReplyForm();

                    const parentComment = this.findComment(parentCommentId);
                    parentComment.replies.push(comment);

                } catch (err) {
                    console.log('Error creating comment:', err);
                    toastr.error(`
                        There was an error while creating the comment.
                        Please try again later.
                    `);
                    setSubmitting(false);
                }
            },

            addCommentReplyFocus() {
                this.commentsInFocus += 1;
            },

            addCommentReplyBlur() {
                this.commentsInFocus -= 1;
            },

            async likeComment(commentId, setSubmitting) {
                setSubmitting(true);
                try {
                    const likes = await Galleries.likeGalleryComment(
                        this.id,
                        commentId,
                        this.userId,
                    );
                    setSubmitting(false);

                    const comment = this.findComment(commentId);
                    comment.likes = likes;

                } catch (err) {
                    console.log('Error liking comment:', err);
                    toastr.error(`
                        There was an error while liking the comment.
                        Please try again later.
                    `);
                    setSubmitting(false);
                }
            },

            async unlikeComment(commentId, setSubmitting) {
                setSubmitting(true);
                try {
                    const userId = this.userId;
                    const likes = await Galleries.unlikeGalleryComment(
                        this.id,
                        commentId,
                        userId,
                    );
                    setSubmitting(false);

                    const comment = this.findComment(commentId);
                    comment.likes = likes;

                } catch (err) {
                    console.log('Error liking comment:', err);
                    toastr.error(`
                        There was an error while liking the comment.
                        Please try again later.
                    `);
                    setSubmitting(false);
                }
            },

            async editComment(commentId, text, setSubmitting, clearText, hideEditForm) {
                setSubmitting(true);
                try {
                    await Galleries.updateGalleryComment(this.id, commentId, {
                        text,
                    });
                    clearText();
                    setSubmitting(false);
                    hideEditForm();

                    const comment = this.findComment(commentId);
                    comment.text = text;

                } catch (err) {
                    console.log('Error editing comment:', err);
                    toastr.error(`
                        There was an error while editing the comment.
                        Please try again later.
                    `);
                    setSubmitting(false);
                }
            },

            async deleteComment(commentId, setSubmitting) {
                setSubmitting(true);
                try {
                    await Galleries.deleteGalleryComment(this.id, commentId);
                    setSubmitting(false);

                    const comment = this.findComment(commentId);
                    if (comment.parentCommentId) {
                        const parentComment = this.findComment(comment.parentCommentId);
                        parentComment.replies = parentComment.replies.filter(c => c.id !== commentId);
                    } else {
                        this.comments = this.comments.filter(c => c.id !== commentId);
                    }

                } catch (err) {
                    console.log('Error deleting comment:', err);
                    toastr.error(`
                        There was an error while deleting the comment.
                        Please try again later.
                    `);
                    setSubmitting(false);
                }
            },

            findComment(commentId) {
                let searchComments;
                searchComments = (id, comments) => {
                    for (const comment of comments) {
                        if (comment.id === id) {
                            return comment;
                        }
                        const subComment = searchComments(id, comment.replies);
                        if (subComment) {
                            return subComment;
                        }
                    }
                };
                return searchComments(commentId, this.comments);
            },

            openLightbox(index) {
                if (!this.lightbox.showing) {
                    this.lightbox.showing = true;
                    this.lightbox.index = index;
                }
            },

            escapeHtml(text) {
                text = text || '';
                text = text.replace(/&/g, '&amp;');
                text = text.replace(/</g, '&lt;');
                text = text.replace(/>/g, '&gt;');
                text = text.replace(/"/g, '&quot;');
                return text;
            }
        },

        watch: {
            '$route': async function() {
                let galleryId = this.$route.params.galleryId;
                if (galleryId) {
                    galleryId = parseInt(galleryId);
                }
                await this.loadGallery(galleryId);
            },

            highlighted: function() {
                if (!this.loading) {
                    this.saveHighlight();
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
    .clear-button {
        border: none;
        background: none;
    }

    .panel {
        background-color: #222222 !important;
    }

    .panel-heading i {
        color: #33ccff;
        padding-right: 7px;
    }

    .form-button {
        background: #33ccff;
        border: #33ccff solid 1px;
        border-radius: 0;
        box-shadow: none;
        box-sizing: border-box;
        color: black;
        display: inline-block;
        font-weight: 400 !important;
        height: 36px;
        line-height: 36px;
        padding: 0 20px;
        text-decoration: none;
        text-shadow: none;
        top: 0; // For when using labels to wrap hidden file inputs
    }
    .form-button:hover {
        background: black;
        color: white;
        border: solid 1px #33ccff;
        outline-offset: 0;
    }

    .gallery-viewer {
        display: flex;
        align-items: flex-start;
    }

    .media-container {
        flex: 1;
        margin-right: 20px;
        .panel-body {
            padding: 20px 20px 0 20px;
        }
        .media-error {
            display: table;
            height: 100px;
            width: 100%;
            text-align: center;
            position: relative;
        }
        .media-error p {
            display: table-cell;
            font-style: italic;
            vertical-align: middle;
        }
        ol {
            list-style-type: none;
            padding: 0;
            margin: 0;
        }
        .media-group {
            width: 100%;
            margin-bottom: 30px;
            .media-wrapper {
                position: relative;
            }
            .media-wrapper:hover {
                .media-buttons {
                    display: inline-block;
                }
            }
            .media {
                display: block;
                margin: 0 auto;
                max-width: 100%;
                max-height: 80vh;
            }
            .media:hover {
                cursor: pointer;
            }
            .media-iframe {
                position: relative;
            }
            .media-buttons-wrapper {
                position: absolute;
                bottom: 0;
                width: 100%;
                text-align: center;
            }
            .media-buttons {
                display: none;
                border-radius: 5px;
                padding: 4px 6px;
                margin-bottom: 10px;
                background-color: rgba(30, 30, 30, 0.4);
                font-size: 16px;
                a, button {
                    color: rgba(255, 255, 255, 0.8);
                    display: inline-block;
                    padding: 4px;
                    margin-right: 4px;
                }
                a:hover, button:hover {
                    color: #33aaff;
                }
                a:last-child, button:last-child {
                    margin-right: 0;
                }
            }
            .media-caption {
                margin-top: 10px;
                text-align: center;
            }
        }
    }

    .right-container {
        flex: 0 0 275px;
        max-width: 275px;
        overflow-wrap: break-word;
        word-wrap: break-word;
    }

    .info-container {
        .panel-heading {
            display: flex;
            position: relative; // Brings box-shadow to forefront (because flexbox)
            align-items: center;
            h2 {
                flex: 1;
            }
        }
        .panel-body {
            padding: 20px 20px 10px 20px;
        }
        h3 {
            margin-top: 0;
        }
        .recruiter-visibility {
            margin-bottom: 10px;
            label {
                display: block;
                top: 0;
            }
            input {
                margin: 0;
            }
        }
        .post-author {
            padding: 0 0 10px 0;
        }
        .post-description {
            margin: 0;
            padding: 0 0 10px 0;
        }
        .post-date {
            font-style: italic;
            padding: 10px 0 5px;
        }
        .info-group {
            padding: 15px 0 10px 0;
        }
        .info-group-label {
            color: #cfcfcf;
            font-size: 16px;
        }
        .gallery-stats {
            i {
                margin-right: 3px;
                width: 14px; // The thumbs-up icon likes to chill at 13px
            }
            button {
                color: #33ccff;
                padding: 1px 0;
                margin-left: 5px;
            }
            button:hover {
                text-decoration: underline;
            }
            button[disabled] {
                color: #b0b0b0;
            }
            button[disabled]:hover {
                text-decoration: none;
            }
        }
        .category {
            margin: 10px 0 0 20px;
        }
        .more-by {
            .info-group-label a {
                margin-left: 0.1rem;
            }
            .gallery-grid {
                margin-top: 10px;
            }
        }
    }

    .comments-container {
        .panel-body {
            padding: 20px;
        }
        ol {
            list-style-type: none;
            padding: 0;
            margin: 0;
        }
        .comment {
            margin-bottom: 30px;
        }
    }

    .loading-container {
        width: 100%;
        padding: 100px 20px;
        text-align: center;
        color: #888;
    }
</style>

<style lang="scss">
    .gallery-viewer {
        .media-container .media-group .media-iframe {
            iframe {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
            }
            .media-iframe-spacer-16-9 {
                content: '';
                display: inline-block;
                padding-top: calc(9 / 16 * 100%);
            }
            .media-iframe-spacer-8-6 {
                content: '';
                display: inline-block;
                padding-top: calc(6 / 8 * 100%);
            }
        }

        .software-list {
            > li {
                width: 33.3%;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// GalleryViewer.vue?66127f21